import { useCallback } from 'react'
import { useReactFlow } from '@xyflow/react'
import { cssLength, resolveNodeBox } from '@genieartinc/schemas'

/**
 * Apply a content aspect to a node that has not been corner-resized.
 * A manual size is left alone so a later image or video does not undo it.
 */
export function useContentAutoSize(nodeId: string, componentType: string) {
  const { updateNode, getNode } = useReactFlow()

  return useCallback((aspect: number) => {
    const node = getNode(nodeId)
    if (!node || !Number.isFinite(aspect) || aspect <= 0) return
    const data = (node.data ?? {}) as Record<string, unknown>
    const state = (data.state ?? {}) as Record<string, unknown>
    if (state.sizeSource === 'manual') return

    const box = resolveNodeBox({
      componentType,
      savedWidth: cssLength(node.style?.width) ?? node.width,
      savedHeight: cssLength(node.style?.height) ?? node.height,
      sizeSource: typeof state.sizeSource === 'string' ? state.sizeSource : 'default',
      contentAspect: aspect,
    })
    const currentWidth = cssLength(node.style?.width)
    const currentHeight = cssLength(node.style?.height)
    if (currentWidth === box.width && currentHeight === box.height && state.sizeSource === 'auto') return

    updateNode(nodeId, (current) => {
      const currentData = (current.data ?? {}) as Record<string, unknown>
      const currentState = (currentData.state ?? {}) as Record<string, unknown>
      if (currentState.sizeSource === 'manual') return current
      return {
        width: box.width,
        height: box.height,
        style: { ...(current.style ?? {}), width: box.width, height: box.height },
        data: {
          ...currentData,
          state: { ...currentState, sizeSource: 'auto' },
        },
      }
    })
  }, [nodeId, componentType, updateNode, getNode])
}
